// Buchhaltung — Offene Garantien (an Hersteller fakturiert, mit Rechnungsnummer, unbezahlt).
// Jun-2026 (User-Wunsch): nur offene Garantien MIT Rechnungsnummer anzeigen.
import { useEffect, useState, useCallback } from "react";
import { Loader2, CheckCircle2, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../../lib/api";
import { fmtDate } from "./shared";

export default function WarrantiesOpenTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/buchhaltung/warranties-open");
            setData(r.data);
        } catch {
            setData({ items: [], summary: { count: 0, total_open: 0, overdue_count: 0 } });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const markPaid = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/buchhaltung/warranties/${id}/mark-paid`);
            toast.success("Garantie-Rechnung als bezahlt markiert");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Konnte nicht markiert werden");
        } finally { setBusyId(null); }
    };

    const items = data?.items || [];
    const s = data?.summary || {};

    return (
        <div data-testid="buchhaltung-warranties-open">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                <div className="rounded-sm border border-gray-200 bg-white px-4 py-3" data-testid="warranties-kpi-total">
                    <div className="text-2xl font-black font-mono-num leading-none text-[#111827]">{formatPrice(s.total_open || 0)}</div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-1.5">Offen gesamt</div>
                </div>
                <div className="rounded-sm border border-gray-200 bg-white px-4 py-3" data-testid="warranties-kpi-count">
                    <div className="text-2xl font-black font-mono-num leading-none text-[#111827]">{s.count || 0}</div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-1.5">Offene Garantien</div>
                </div>
                <div className="rounded-sm border border-gray-200 bg-white px-4 py-3" data-testid="warranties-kpi-overdue">
                    <div className={`text-2xl font-black font-mono-num leading-none ${s.overdue_count > 0 ? "text-red-600" : "text-[#111827]"}`}>{s.overdue_count || 0}</div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-1.5">Überfällig</div>
                </div>
            </div>

            <div className="flex justify-end mb-2">
                <button type="button" onClick={load} className="text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827] inline-flex items-center gap-1" data-testid="warranties-refresh">
                    <RefreshCw className="w-3 h-3" /> Aktualisieren
                </button>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-gray-500 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Lade offene Garantien…</div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-gray-500" data-testid="warranties-empty">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                    Keine offenen Garantie-Rechnungen (mit Rechnungsnummer) — alles ausgeglichen.
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-sm overflow-auto" data-testid="warranties-table">
                    <table className="w-full text-[12px]">
                        <thead className="bg-gray-100 text-gray-600">
                            <tr>
                                <th className="text-left px-3 py-2">Rechnung</th>
                                <th className="text-left px-3 py-2">Hersteller</th>
                                <th className="text-left px-3 py-2">Fahrzeug / Auftrag</th>
                                <th className="text-left px-3 py-2">Fällig</th>
                                <th className="text-right px-3 py-2">Überfällig</th>
                                <th className="text-right px-3 py-2">Offen</th>
                                <th className="text-right px-3 py-2">Aktion</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => (
                                <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`warranties-row-${it.id}`}>
                                    <td className="px-3 py-2 font-mono-num font-bold text-[#111827]">{it.invoice_number}</td>
                                    <td className="px-3 py-2"><span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> {it.warranty_manufacturer}</span></td>
                                    <td className="px-3 py-2 truncate max-w-[220px]">{it.vehicle_label} <span className="text-gray-400">· {it.order_number}</span></td>
                                    <td className="px-3 py-2">{fmtDate(it.due_date)}</td>
                                    <td className={`px-3 py-2 text-right font-mono-num font-bold ${it.days_overdue > 60 ? "text-red-600" : it.days_overdue > 0 ? "text-amber-700" : "text-gray-500"}`}>
                                        {it.days_overdue > 0 ? `${it.days_overdue} T` : "—"}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono-num font-bold">{formatPrice(it.open_amount)}</td>
                                    <td className="px-3 py-2 text-right">
                                        <button type="button" onClick={() => markPaid(it.id)} disabled={busyId === it.id}
                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 border border-emerald-300 rounded-sm px-2.5 py-1 bg-white"
                                            data-testid={`warranties-mark-paid-${it.id}`}>
                                            {busyId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5" /> Bezahlt</>}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
